import { Injectable, Signal, computed, signal } from '@angular/core';
import { ApiBaseService } from '@shared/services/entities/api-base.service';
import { ModuleModel } from '@shared/models/entities/module.model';

@Injectable({ providedIn: 'root' })
export class ModulesService extends ApiBaseService<ModuleModel> {
  /**
   * Cache `route -> etiqueta visible`, alimentado por cualquier `search` o
   * `update` exitoso. Es la fuente del título que cada módulo muestra en su
   * encabezado (`moduleTitle`), para que el nombre del menú y el del módulo
   * salgan SIEMPRE de la misma columna (`modules.label`): si el admin cambia
   * la etiqueta visible, cambia en los dos lados sin tocar código.
   *
   * Es un signal aparte de `listData` a propósito: `listData` lo pisan otras
   * pantallas (p. ej. Roles busca módulos con sus propios filtros) y el
   * título no debe parpadear por eso.
   */
  private readonly labelsByRoute = signal<Record<string, string>>({});
  /** Icono (Material) de cada módulo por ruta, para el encabezado de la pantalla. */
  private readonly iconsByRoute = signal<Record<string, string>>({});
  private labelsRequested = false;

  constructor() {
    super('modules'); // ← recurso REST: /modules/search, POST /modules, PATCH /modules
  }

  override async search(data: any): Promise<Utils.ApiResponse<ModuleModel>> {
    const result = await super.search(data);
    this.mergeLabels(Array.isArray(result.data) ? result.data : []);
    return result;
  }

  /**
   * Sube cada vez que se crea o edita un módulo (p. ej. se activa o
   * desactiva). El menú lateral lo escucha para recargarse sin refrescar.
   */
  readonly revision = signal(0);

  override async update(item: ModuleModel): Promise<Utils.ApiResponse<ModuleModel>> {
    const result = await super.update(item);
    this.mergeLabels(Array.isArray(result.data) ? result.data : [item]);
    this.revision.update(n => n + 1);
    return result;
  }

  override async create(item: ModuleModel): Promise<Utils.ApiResponse<ModuleModel>> {
    const result = await super.create(item);
    this.revision.update(n => n + 1);
    return result;
  }

  private mergeLabels(modules: ModuleModel[]): void {
    if (!modules.length) return;
    const next = { ...this.labelsByRoute() };
    for (const m of modules) {
      if (m.route && (m.label || m.name)) {
        next[m.route] = (m.label || m.name)!;
      }
    }
    this.labelsByRoute.set(next);
    const icons = { ...this.iconsByRoute() };
    for (const m of modules) {
      if (m.route && m.icon) icons[m.route] = m.icon;
    }
    this.iconsByRoute.set(icons);
  }

  /**
   * Icono del módulo de la ruta `path`: coincidencia exacta o, si no, la ruta
   * de módulo más larga que sea prefijo (p. ej. /interventions/remission/123
   * no es módulo, pero /interventions sí). '' si no hay.
   */
  moduleIcon(path: Signal<string> | string): Signal<string> {
    this.ensureLabels();
    return computed(() => {
      const p = String(typeof path === 'string' ? path : path()).split(/[?#]/)[0].replace(/\/+$/, '') || '/';
      const icons = this.iconsByRoute();
      if (icons[p]) return icons[p];
      let best = '';
      for (const route of Object.keys(icons)) {
        const r = route.replace(/\/+$/, '');
        if (r && r !== '/' && (p === r || p.startsWith(r + '/')) && r.length > best.length) best = r;
      }
      if (best) return icons[best];
      // Inicio: la pantalla vive en /dashboard pero el módulo se registra como '/'.
      if (p === '/dashboard') return icons['/'] || 'home';
      return '';
    });
  }

  /**
   * Título del módulo cuya ruta es `route`, con `fallback` mientras la tabla
   * no ha cargado (o si el módulo no existe en ella). Uso típico en el
   * componente raíz de un módulo:
   *
   *     title = this.modulesService.moduleTitle('/interventions', 'Intervenciones');
   *     // template: <h2>{{ title() }}</h2>
   */
  moduleTitle(route: string, fallback: string): Signal<string> {
    this.ensureLabels();
    return computed(() => this.labelsByRoute()[route] || fallback);
  }

  /** Carga la tabla una sola vez si nadie la ha pedido aún (best-effort). */
  private ensureLabels(): void {
    if (this.labelsRequested) return;
    this.labelsRequested = true;
    this.search({ searchType: 'default', condition: { deleted: false } }).catch(err => {
      // Sin red o sin permiso: se quedan los fallbacks hardcodeados.
      this.labelsRequested = false;
      console.warn('[modules] No se pudieron cargar las etiquetas de módulos:', err);
    });
  }
}
